// ═══════════════════════════════════════════════════════════════
// Business Owner Portal — Status Check-In
// View checks targeting org, respond only (no create/edit/delete)
// ═══════════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Textarea, TextInput,
  Collapse, ActionIcon, Loader,
} from '../../../components/gab/GabComponents';
import { notifications } from '../../../lib/notifications';
import {
  IconRefresh, IconChevronDown, IconChevronUp, IconSend,
} from '@tabler/icons-react';
import { useMyOrg } from '../../../hooks/useMyOrg';
import { useMyStatusResponses } from '../../../hooks/useMyOrgData';
import { useMainStreetOpsStore } from '../../../store/mainstreetOpsStore';
import type { MSStatusCheck, MSStatusResponse } from '../../../store/mainstreetOpsStore';
import { useAuthStore } from '../../../store/authStore';
import { fmtDate, fmtDateTime } from '../../../lib/dateFormat';

export default function PortalStatusCheckIn() {
  const myOrg = useMyOrg();
  const activeIncident = useAuthStore(s => s.activeIncident);
  const {
    statusChecks, loading,
    loadStatusChecks, loadStatusResponses, submitResponse,
  } = useMainStreetOpsStore();
  const myResponses = useMyStatusResponses();

  const [expandedIds, setExpandedIds] = useState<Record<number, boolean>>({});
  const [respondCheckId, setRespondCheckId] = useState<number | null>(null);
  const [respText, setRespText] = useState('');
  const [respNotes, setRespNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Filter to checks targeting this org
  const relevantChecks = useMemo(() => {
    if (!myOrg) return [];
    const orgId = String(myOrg.id);
    const sector = myOrg.sectorType ?? '';

    return statusChecks
      .filter(sc => {
        if (activeIncident && String(sc.incidentId) !== String(activeIncident.id)) return false;
        if (sc.targetType === 'all') return true;
        if (sc.targetType === 'sector' && sc.targetSectors.split(',').includes(sector)) return true;
        if (sc.targetType === 'specific' && sc.targetOrgs.split(',').includes(orgId)) return true;
        return false;
      })
      .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
  }, [statusChecks, myOrg, activeIncident]);

  const respondedCheckIds = useMemo(
    () => new Set(myResponses.map(r => r.statusCheckId)),
    [myResponses],
  );

  function myResponseForCheck(checkId: number): MSStatusResponse | undefined {
    return myResponses.find(r => String(r.statusCheckId) === String(checkId));
  }

  function toggleExpand(id: number) {
    setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }));
  }

  async function handleRespond(check: MSStatusCheck) {
    if (!respText.trim()) {
      notifications.show({ color: 'red', title: 'Required', message: 'Please provide a response.' });
      return;
    }
    if (!myOrg) return;
    setSubmitting(true);
    await submitResponse({
      statusCheckId: String(check.id),
      orgId: String(myOrg.id),
      orgName: myOrg.name,
      response: respText.trim(),
      notes: respNotes.trim(),
      respondedAt: new Date().toISOString(),
    });
    setSubmitting(false);
    notifications.show({ color: 'green', title: 'Submitted', message: 'Response submitted.' });
    setRespondCheckId(null);
    setRespText('');
    setRespNotes('');
  }

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Status Check-In</Title>
        <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
          loading={loading} onClick={() => { loadStatusChecks(); loadStatusResponses(); }}>
          Refresh
        </Button>
      </Group>

      <Text size="xs" c="dimmed" mb="md">
        {relevantChecks.length} check{relevantChecks.length !== 1 ? 's' : ''} targeting your organization
      </Text>

      {loading && relevantChecks.length === 0 && (
        <Group justify="center" p="xl"><Loader size="sm" /></Group>
      )}

      {relevantChecks.length === 0 && !loading && (
        <Paper p="xl" withBorder>
          <Text ta="center" c="dimmed">No status checks targeting your organization.</Text>
        </Paper>
      )}

      {relevantChecks.map(check => {
        const hasResponded = respondedCheckIds.has(String(check.id));
        const myResp = myResponseForCheck(check.id);
        const isExpanded = expandedIds[check.id];
        const isDeadlinePassed = check.deadline && new Date(check.deadline) < new Date();

        return (
          <Paper key={check.id} p="sm" mb="sm" withBorder
            style={{ borderLeft: `3px solid ${hasResponded ? 'var(--mantine-color-green-5)' : isDeadlinePassed ? 'var(--mantine-color-red-5)' : 'var(--mantine-color-blue-5)'}` }}>
            <Group justify="space-between" wrap="nowrap">
              <div style={{ flex: 1 }}>
                <Group gap="xs" mb={4}>
                  <Text fw={600} size="sm">{check.title}</Text>
                  <Badge size="xs" color={hasResponded ? 'green' : check.status === 'open' ? 'blue' : 'gray'}>
                    {hasResponded ? 'Responded' : check.status}
                  </Badge>
                  {!hasResponded && isDeadlinePassed && (
                    <Badge size="xs" color="red" variant="filled">OVERDUE</Badge>
                  )}
                </Group>
                <Text size="xs" c="dimmed">{check.question}</Text>
                <Group gap="xs" mt={4}>
                  <Text size="xs" c="dimmed">By {check.authorName}</Text>
                  {check.deadline && (
                    <Text size="xs" c={isDeadlinePassed ? 'red' : 'dimmed'}>
                      Deadline: {fmtDate(check.deadline)}
                    </Text>
                  )}
                  <Text size="xs" c="dimmed">{fmtDateTime(check.createdAt)}</Text>
                </Group>
              </div>
              <Group gap={4}>
                {!hasResponded && check.status === 'open' && (
                  <ActionIcon size="sm" variant="subtle" color="blue"
                    onClick={() => setRespondCheckId(respondCheckId === check.id ? null : check.id)}>
                    <IconSend size={14} />
                  </ActionIcon>
                )}
                {hasResponded && (
                  <ActionIcon size="sm" variant="subtle"
                    onClick={() => toggleExpand(check.id)}>
                    {isExpanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                  </ActionIcon>
                )}
              </Group>
            </Group>

            {/* Respond inline */}
            {!hasResponded && (
              <Collapse in={respondCheckId === check.id}>
                <Paper p="sm" mt="sm" bg="var(--mantine-color-gray-0)" radius="sm">
                  <Text size="xs" fw={600} mb="xs">Submit Response for {myOrg?.name}</Text>
                  <Textarea value={respText} onChange={e => setRespText(e.target.value)}
                    placeholder="Your response..." size="xs" mb="xs" autosize minRows={2} />
                  <TextInput value={respNotes} onChange={e => setRespNotes(e.target.value)}
                    placeholder="Additional notes (optional)" size="xs" mb="xs" />
                  <Group justify="flex-end">
                    <Button size="xs" variant="subtle" onClick={() => setRespondCheckId(null)}>Cancel</Button>
                    <Button size="xs" loading={submitting} onClick={() => handleRespond(check)}>Submit</Button>
                  </Group>
                </Paper>
              </Collapse>
            )}

            {/* Show my response */}
            {hasResponded && (
              <Collapse in={isExpanded}>
                <Paper p="sm" mt="sm" bg="var(--mantine-color-green-0)" radius="sm">
                  <Text size="xs" fw={600} mb={4}>Your Response</Text>
                  <Text size="sm">{myResp?.response}</Text>
                  {myResp?.notes && <Text size="xs" c="dimmed" mt={4}>Notes: {myResp.notes}</Text>}
                  <Text size="xs" c="dimmed" mt={4}>{fmtDateTime(myResp?.respondedAt ?? '')}</Text>
                </Paper>
              </Collapse>
            )}
          </Paper>
        );
      })}
    </div>
  );
}
